import Admin from "../Admin";
import PageHeader from "../../../Component/PageHeader";
import MUIButton from "@mui/material/Button";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUISaveButton from "@mui/icons-material/Save";
import MUILoadingButton from "@mui/lab/LoadingButton";
import Menu from "../../../Component/Menu";
import DialogConfirm from "../../../Component/Dialog/DialogConfirm";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import Validator from "../../../Utility/Validator";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import CardAdminModifierCodeGeneral from "../../../Component/Card/Admin/ModifierCode/CardAdminModifierCodeGeneral";
import ModifierCode from "../../../Utility/Crud/ModifierCode";
import ModifierCodeService from "../../../Seating/Security/ModifierCodeService/modifierCodeService";

export default class ModifierCodeCreateEdit extends Admin {
  constructor(props) {
    super(props);

    this.state.modifierCode = null;
    this.state.isProcessing = false;
    this.state.isUpdate = !!props.match.params.id;
    this.state.confirmDialogOpen = false;
    this.state.validationIssues = {};
  }

  /**
   * Set the modifierCode in the state.
   */
  afterComponentDidMount() {
    const modifierCodeId = this.props.match.params.id
      ? +this.props.match.params.id
      : null;
    if (modifierCodeId !== null) {
      this.setState({
        modifierCode: this.context.modifierCodesIndexed[modifierCodeId],
      });
    }
  }

  /**
   * Creates the modifierCode with the current properties in state.modifierCode.
   */
  async createModifierCode() {
    this.setState({
      isProcessing: true,
    });

    return ModifierCodeService.createModifierCode(this.state.modifierCode)
      .then((modifierCode) => {
        this.context.updateIndexed("modifierCodesIndexed", modifierCode);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Modifier Code created successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        // JZ. We need to test and make sure that Sentry still captures
        // errors when we catch them in the application.
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error creating a new Modifier Code.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  /**
   * Updates the modifierCode with the current properties in state.modifierCode.
   */
  async updateModifierCode() {
    this.setState({
      isProcessing: true,
    });

    return ModifierCodeService.updateModifierCode(this.state.modifierCode)
      .then((modifierCode) => {
        this.context.updateIndexed("modifierCodesIndexed", modifierCode);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Modifier Code updated successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error updating the Modifier Code.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  async deleteModifierCode() {
    this.setState({
      isProcessing: true,
    });

    return ModifierCodeService.deleteModifierCode(this.state.modifierCode.id)
      .then(() => {
        this.context.deleteIndexed(
          "modifierCodesIndexed",
          this.state.modifierCode.id,
        );
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Modifier Code deleted successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error deleting the Modifier Code.",
          { variant: "error" },
        );
        this.setState({
          confirmDialogOpen: false,
        });
      });
  }

  /**
   * Render the content.
   */
  renderContent() {
    const handleChange = (modifierCode) => {
      this.setState({
        modifierCode: {
          ...this.state.modifierCode,
          ...modifierCode,
        },
      });
    };

    return (
      <>
        <CardAdminModifierCodeGeneral
          modifierCode={this.state.modifierCode}
          onChange={handleChange.bind(this)}
          validationIssues={this.state.validationIssues}
        />
        <DialogConfirm
          open={this.state.confirmDialogOpen}
          onClose={() => this.setState({ confirmDialogOpen: false })}
          onConfirm={() => this.deleteModifierCode()}
          text="Are you sure you want to delete this Modifier Code? This cannot be undone."
          header="Delete Modifier Code"
          loading={this.state.isProcessing}
        />
      </>
    );
  }

  /**
   * Renders the header.
   */
  renderHeader() {
    let title;
    let subtitle;
    const buttonText = this.state.isUpdate ? "Save" : "Create";

    if (!this.state.isUpdate) {
      title = "Create Modifier Code";
      subtitle = null;
    } else {
      title = "Edit Modifier Code";
      subtitle =
        this.state.modifierCode &&
        ModifierCode.getDisplayName(this.state.modifierCode) +
          " • " +
          this.state.modifierCode.id;
    }

    const handleClickBack = () => {
      this.props.history.goBack();
    };

    const handleClickSave = () => {
      const validationIssues = Validator.validate(this.state.modifierCode, {
        code: {
          required: true,
        },
        description: {
          required: true,
        },
      });

      this.setState({
        validationIssues: validationIssues,
      });

      if (Object.keys(validationIssues).length > 0) {
        return NotistackEnqueueSnackbar(
          "Please correct the displayed issues.",
          { variant: "error" },
        );
      }

      if (Object.keys(validationIssues).length === 0) {
        if (this.state.isUpdate) {
          this.updateModifierCode();
        } else {
          this.createModifierCode();
        }
      }
    };

    const handleDelete = () => {
      this.setState({
        confirmDialogOpen: true,
      });
    };

    const buttons = [
      <MUIButton onClick={handleClickBack}>Back</MUIButton>,
      <MUILoadingButton
        loading={this.state.isProcessing}
        loadingPosition="start"
        onClick={handleClickSave}
        variant="contained"
        startIcon={<MUISaveButton />}
      >
        {buttonText}
      </MUILoadingButton>,
    ];

    if (this.state.isUpdate) {
      const menuItems = [
        <MUIMenuItem onClick={handleDelete}>
          <MUIListItemIcon>
            <MUIDeleteIcon fontSize="small" />
          </MUIListItemIcon>
          Delete
        </MUIMenuItem>,
      ];
      buttons.push(<Menu menuItems={menuItems} />);
    }

    return <PageHeader title={title} subtitle={subtitle} buttons={buttons} />;
  }
}
